import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getNurturingRowKey,
  NurturingPreferenceRow,
  PickDateFilterType,
} from '../apiService/runLogsApiService';
import { formatDateForDisplay } from 'utils/dateUtils';
import NurturingDateFilterControls from './NurturingDateFilterControls';

export interface NurturingPreferencesModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  refreshLoading: boolean;
  error: string | null;
  rows: NurturingPreferenceRow[];
  headerRow: string[];
  ttl: number | null;
  soldWaitDays: number | null;
  unsoldWaitDays: number | null;
  batchSizeByRowKey: Record<string, number>;
  onUpdateBatchSize: (rowKey: string, value: number) => void;
  isAllChecked: boolean;
  onAllCheckedChange: (checked: boolean) => void;
  dateFilterType: Exclude<PickDateFilterType, 'ALL'> | null;
  onDateFilterTypeChange: (filterType: Exclude<PickDateFilterType, 'ALL'> | null) => void;
  startDate: string | null;
  onStartDateChange: (date: string | null) => void;
  endDate: string | null;
  onEndDateChange: (date: string | null) => void;
  onSavePreferences: () => Promise<void>;
  onRefresh: () => void;
  saveLoading: boolean;
  canSavePreferences: boolean;
}

export function NurturingPreferencesModal({
  open,
  onClose,
  loading,
  refreshLoading,
  error,
  rows,
  headerRow,
  ttl,
  soldWaitDays,
  unsoldWaitDays,
  batchSizeByRowKey,
  onUpdateBatchSize,
  isAllChecked,
  onAllCheckedChange,
  dateFilterType,
  onDateFilterTypeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onSavePreferences,
  onRefresh,
  saveLoading,
  canSavePreferences,
}: NurturingPreferencesModalProps) {
  /** Local string state so users can type freely; committed on valid parse. */
  const [inputValues, setInputValues] = React.useState<Record<string, string>>({});
  const todayIsoDate = React.useMemo(() => new Date().toISOString(), []);

  /** Sync input strings whenever rows or batch sizes are refreshed from the API. */
  React.useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const row of rows) {
      const rowKey = getNurturingRowKey(row);
      next[rowKey] = String(batchSizeByRowKey[rowKey] ?? row.batch_size);
    }
    setInputValues(next);
  }, [open, rows, batchSizeByRowKey]);

  const handleBatchSizeChange = (rowKey: string, raw: string) => {
    setInputValues(prev => ({ ...prev, [rowKey]: raw }));
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      onUpdateBatchSize(rowKey, parsed);
    }
  };

  const handleBatchSizeBlur = (rowKey: string) => {
    const raw = inputValues[rowKey] ?? '';
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputValues(prev => ({
        ...prev,
        [rowKey]: String(batchSizeByRowKey[rowKey] ?? 0),
      }));
    }
  };

  const headers =
    headerRow.length >= 4
      ? headerRow
      : [
          'Segment',
          'Available for Nurturing Candidate',
          'Maiq Lead Type',
          'Batch Size',
        ];
  const showEarliestAvailableDateColumn =
    headers.length >= 5 ||
    rows.some(
      row =>
        Boolean(row.earliest_available_date) ||
        Boolean(row.earliestAvailableForNurturingDate),
    );
  const getEarliestDateLabel = (
    row: NurturingPreferenceRow,
  ): string => {
    const rawDate =
      row.earliest_available_date ??
      row.earliestAvailableForNurturingDate;
    if (!rawDate) return '—';
    return formatDateForDisplay(rawDate, 'dd MMM yyyy') || '—';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      aria-labelledby="nurturing-preferences-modal-title"
    >
      <DialogTitle id="nurturing-preferences-modal-title">Preferences</DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <>
            {/* Read-only meta info — TTL and wait days */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                gap: 2,
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  TTL (seconds)
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {ttl ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Sold Wait Days
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {soldWaitDays ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Unsold Wait Days
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {unsoldWaitDays ?? '—'}
                </Typography>
              </Box>

              {/* Refresh icon — top-right of meta box */}
              <Tooltip title="Refresh preferences">
                <span style={{ position: 'absolute', top: 6, right: 6 }}>
                  <IconButton
                    size="small"
                    onClick={onRefresh}
                    disabled={refreshLoading || loading}
                    aria-label="Refresh preferences"
                  >
                    {refreshLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Date Filter
              </Typography>
              <NurturingDateFilterControls
                showAllOption
                isAllChecked={isAllChecked}
                onAllCheckedChange={onAllCheckedChange}
                dateFilterType={dateFilterType}
                onDateFilterTypeChange={onDateFilterTypeChange}
                startDate={startDate}
                onStartDateChange={onStartDateChange}
                endDate={endDate}
                onEndDateChange={onEndDateChange}
                disabled={loading || refreshLoading || saveLoading}
                maxDate={todayIsoDate}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1150 }}>
                <TableHead>
                  <TableRow>
                    {headers.map((h, i) => (
                      <TableCell key={i} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length}
                        align="center"
                        sx={{ py: 3, color: 'text.secondary' }}
                      >
                        No segments
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => {
                      const rowKey = getNurturingRowKey(row);
                      return (
                        <TableRow key={rowKey}>
                          <TableCell>{row.segment_name}</TableCell>
                          <TableCell>{row.available_for_nurturing_candidate}</TableCell>
                          {showEarliestAvailableDateColumn && (
                            <TableCell>
                              {getEarliestDateLabel(row)}
                            </TableCell>
                          )}
                          <TableCell>{row.maiq_lead_type ?? '—'}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, style: { textAlign: 'right' } }}
                              value={inputValues[rowKey] ?? ''}
                              onChange={e =>
                                handleBatchSizeChange(rowKey, e.target.value)
                              }
                              onBlur={() => handleBatchSizeBlur(rowKey)}
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onSavePreferences().then(() => onClose()).catch(() => {})}
          disabled={saveLoading || !canSavePreferences}
          startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saveLoading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
